<template>
  <div :class="{'text-muted': notification.unreaded === false}">
    <b-link
      v-if="notification.type === 'order_new_comment'"
      @click="handleOrderNavigation(notification)"
    >
      <b-media>
        <template #aside>
          <b-avatar
            size="32"
            variant="primary"
          >
            <feather-icon icon="MessageSquareIcon" />
          </b-avatar>
        </template>
        <p class="media-heading">
          <span class="font-weight-bolder">
            {{ sprintf($t(`notifications.${notification.type}.title`), notification.data.orderNumber) }}
          </span>
        </p>
        <p class="notification-text mb-0">
          <b-button
            v-show="notification.unreaded"
            variant="flat-success"
            class="float-right"
            @click.stop="markAsRead(notification)"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>
          {{ $t(`notifications.${notification.type}.text`) }}
        </p>
        <p class="notification-text text-muted">
          {{ elapsedTime(notification.createdAt) }}
        </p>
      </b-media>
    </b-link>
    <b-link
      v-if="notification.type === 'system_new_version'"
      :class="{'opacity-50': notification.unreaded === false}"
      @click="handleSystemVersionNotification(notification)"
    >
      <b-media>
        <template #aside>
          <b-avatar
            size="32"
            variant="danger"
          >
            <feather-icon icon="AlertCircleIcon" />
          </b-avatar>
        </template>
        <p class="media-heading">
          <span class="font-weight-bolder">
            {{ sprintf($t(`notifications.${notification.type}.title`), notification.data.orderNumber) }}
          </span>
          <small class="whitespace-no-wrap float-right">
            {{ elapsedTime(notification.createdAt) }}
          </small>
        </p>
        <p class="notification-text mb-0">
          {{ $t(`notifications.${notification.type}.text`) }}
        </p>
        <p class="notification-text text-muted">
          {{ elapsedTime(notification.createdAt) }}
        </p>
      </b-media>
    </b-link>
    <b-link
      v-if="notification.type === 'team_assigned_to_order'"
      :class="{'opacity-50': notification.unreaded === false}"
      @click="handleAssignedNotification(notification)"
    >
      <b-media>
        <template #aside>
          <b-avatar
            size="32"
            variant="danger"
          >
            <feather-icon icon="FileTextIcon" />
          </b-avatar>
        </template>
        <p class="media-heading">
          <span class="font-weight-bolder">
            {{ sprintf($t(`notifications.${notification.type}.title`), notification.data.orderNumber) }}
          </span>
        </p>
        <p class="notification-text mb-0">
          <b-button
            v-show="notification.unreaded"
            variant="flat-success"
            class="float-right"
            @click.stop="markAsRead(notification)"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>
          {{ $t(`notifications.${notification.type}.text`) }}
        </p>
        <p class="notification-text text-muted">
          {{ elapsedTime(notification.createdAt) }}
        </p>
      </b-media>
    </b-link>
    <b-link
      v-if="notification.type === 'team_unassigned_from_order'"
      :class="{'opacity-50': notification.unreaded === false}"
      @click="handleOrderListNavigation(notification)"
    >
      <b-media>
        <template #aside>
          <b-avatar
            size="32"
            variant="warning"
          >
            <feather-icon icon="FileMinusIcon" />
          </b-avatar>
        </template>
        <p class="media-heading">
          <span class="font-weight-bolder">
            {{ sprintf($t(`notifications.${notification.type}.title`), notification.data.orderNumber) }}
          </span>
        </p>
        <p class="notification-text mb-0">
          <b-button
            v-show="notification.unreaded"
            variant="flat-success"
            class="float-right"
            @click.stop="markAsRead(notification)"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>
          {{ $t(`notifications.${notification.type}.text`) }}
        </p>
        <p class="notification-text text-muted">
          {{ elapsedTime(notification.createdAt) }}
        </p>
      </b-media>
    </b-link>
    <b-link
      v-if="notification.type === 'team_is_free'"
      :class="{'opacity-50': notification.unreaded === false}"
      @click="handleOrderListNavigation(notification)"
    >
      <b-media>
        <template #aside>
          <b-avatar
            size="32"
            variant="danger"
          >
            <feather-icon icon="UsersIcon" />
          </b-avatar>
        </template>
        <p class="media-heading">
          <span class="font-weight-bolder">
            {{ sprintf($t(`notifications.${notification.type}.title`), notification.data.teamDefinition) }}
          </span>
        </p>
        <p class="notification-text mb-0">
          <b-button
            v-show="notification.unreaded"
            variant="flat-success"
            class="float-right"
            @click.stop="markAsRead(notification)"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>
          {{ $t(`notifications.${notification.type}.text`) }}
        </p>
        <p class="notification-text text-muted">
          {{ elapsedTime(notification.createdAt) }}
        </p>
      </b-media>
    </b-link>
    <b-link
      v-if="notification.type === 'external_order_request_submitted'"
      :class="{'opacity-50': notification.unreaded === false}"
      @click="handleExternalOrderRequestNavigation(notification)"
    >
      <b-media>
        <template #aside>
          <b-avatar
            size="32"
            variant="warning"
          >
            <feather-icon icon="ClipboardIcon" />
          </b-avatar>
        </template>
        <p class="media-heading">
          <span class="font-weight-bolder">
            {{ sprintf($t(`notifications.${notification.type}.title`), notification.data.requestNumber, notification.data.partnerName) }}
          </span>
        </p>
        <p class="notification-text mb-0">
          <b-button
            v-show="notification.unreaded"
            variant="flat-success"
            class="float-right"
            @click.stop="markAsRead(notification)"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>
          {{ $t(`notifications.${notification.type}.text`) }}
        </p>
        <p class="notification-text text-muted">
          {{ elapsedTime(notification.createdAt) }}
        </p>
      </b-media>
    </b-link>
    <b-link
      v-if="notification.type === 'external_order_request_changes_requested'"
      :class="{'opacity-50': notification.unreaded === false}"
      @click="handleExternalOrderRequestNavigation(notification)"
    >
      <b-media>
        <template #aside>
          <b-avatar
            size="32"
            variant="warning"
          >
            <feather-icon icon="ClipboardIcon" />
          </b-avatar>
        </template>
        <p class="media-heading">
          <span class="font-weight-bolder">
            {{ sprintf($t(`notifications.${notification.type}.title`), notification.data.requestNumber) }}
          </span>
        </p>
        <p class="notification-text mb-0">
          <b-button
            v-show="notification.unreaded"
            variant="flat-success"
            class="float-right"
            @click.stop="markAsRead(notification)"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>
          {{ $t(`notifications.${notification.type}.text`) }}
        </p>
        <p class="notification-text text-muted">
          {{ elapsedTime(notification.createdAt) }}
        </p>
      </b-media>
    </b-link>
    <b-link
      v-if="notification.type === 'order_blocked_for_routing_invalid_address'"
      :class="{'opacity-50': notification.unreaded === false}"
      @click="handleOrderNavigation(notification)"
    >
      <b-media>
        <template #aside>
          <b-avatar
            size="32"
            variant="warning"
          >
            <feather-icon icon="AlertTriangleIcon" />
          </b-avatar>
        </template>
        <p class="media-heading">
          <span class="font-weight-bolder">
            {{ sprintf($t(`notifications.${notification.type}.title`), notification.data.orderNumber) }}
          </span>
        </p>
        <p class="notification-text mb-0">
          <b-button
            v-show="notification.unreaded"
            variant="flat-success"
            class="float-right"
            @click.stop="markAsRead(notification)"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>
          {{ $t(`notifications.${notification.type}.text`) }}
        </p>
        <p class="notification-text text-muted">
          {{ elapsedTime(notification.createdAt) }}
        </p>
      </b-media>
    </b-link>
    <b-link
      v-if="notification.type === 'schedule_changed'"
      :class="{'opacity-50': notification.unreaded === false}"
      @click="handleScheduleNotification(notification)"
    >
      <b-media>
        <template #aside>
          <b-avatar
            size="32"
            variant="info"
          >
            <feather-icon icon="CalendarIcon" />
          </b-avatar>
        </template>
        <p class="media-heading">
          <span class="font-weight-bolder">
            {{ $t(`notifications.${notification.type}.title`) }}
          </span>
        </p>
        <p class="notification-text mb-0">
          <b-button
            v-show="notification.unreaded"
            variant="flat-success"
            class="float-right"
            @click.stop="markAsRead(notification)"
          >
            <feather-icon icon="CheckIcon" />
          </b-button>
          {{ formatScheduleChangeText(notification) }}
        </p>
        <p class="notification-text text-muted">
          {{ elapsedTime(notification.createdAt) }}
        </p>
      </b-media>
    </b-link>
  </div>
</template>
<script>
import {
  BLink, BMedia, BAvatar, BButton,
} from 'bootstrap-vue'

import { sprintf } from 'printj'
import dayjs from 'dayjs'

const ELAPSED_TRANSLATIONS = {
  year: {
    single: 'elapsed-time.year',
    few: 'elapsed-time.years-lt-5',
    many: 'elapsed-time.years',
  },
  day: {
    single: 'elapsed-time.day',
    few: 'elapsed-time.days-lt-5',
    many: 'elapsed-time.days',
  },
  hour: {
    single: 'elapsed-time.hour',
    few: 'elapsed-time.hours-lt-5',
    many: 'elapsed-time.hours',
  },
  minute: {
    single: 'elapsed-time.minute',
    few: 'elapsed-time.minutes-lt-5',
    many: 'elapsed-time.minutes',
  },
  second: {
    single: 'elapsed-time.second',
    few: 'elapsed-time.seconds-lt-5',
    many: 'elapsed-time.seconds',
  },
}

export default {
  name: 'NotificationItem',
  components: {
    BLink, BMedia, BAvatar, BButton,
  },
  props: {
    notification: {
      type: Object,
      default: () => {},
    },
  },
  methods: {
    sprintf,
    elapsedTime(startTime) {
      if (!startTime) {
        return this.$t('elapsed-time.now')
      }

      const createdAt = dayjs(startTime)

      if (!createdAt.isValid()) {
        return this.$t('elapsed-time.now')
      }

      let diffInSeconds = dayjs().diff(createdAt, 'second')
      if (diffInSeconds <= 0) {
        return this.$t('elapsed-time.now')
      }

      const secondsInMinute = 60
      const secondsInHour = secondsInMinute * 60
      const secondsInDay = secondsInHour * 24
      const secondsInYear = secondsInDay * 365

      const years = Math.floor(diffInSeconds / secondsInYear)
      if (years > 0) {
        return this.formatElapsedValue(years, 'year')
      }

      diffInSeconds %= secondsInYear
      const days = Math.floor(diffInSeconds / secondsInDay)
      if (days > 0) {
        return this.formatElapsedValue(days, 'day')
      }

      diffInSeconds %= secondsInDay
      const hours = Math.floor(diffInSeconds / secondsInHour)
      if (hours > 0) {
        return this.formatElapsedValue(hours, 'hour')
      }

      diffInSeconds %= secondsInHour
      const minutes = Math.floor(diffInSeconds / secondsInMinute)
      if (minutes > 0) {
        return this.formatElapsedValue(minutes, 'minute')
      }

      const seconds = diffInSeconds % secondsInMinute
      if (seconds > 0) {
        return this.formatElapsedValue(seconds, 'second')
      }

      return this.$t('elapsed-time.now')
    },
    formatElapsedValue(value, unit) {
      const mapping = ELAPSED_TRANSLATIONS[unit]
      const teens = value % 100
      const lastDigit = value % 10

      if (lastDigit === 1 && teens !== 11) {
        return `${value} ${this.$t(mapping.single)}`
      }
      if (lastDigit >= 2 && lastDigit <= 4 && (teens < 12 || teens > 14)) {
        return `${value} ${this.$t(mapping.few)}`
      }
      return `${value} ${this.$t(mapping.many)}`
    },
    markAsRead(notification) {
      if (!notification.unreaded) {
        return
      }
      this.$store.commit('auth/MARK_NOTIFICATION_AS_READ', notification.id)
      this.$store.dispatch('auth/markNotificationAsRead', notification.id)
    },
    handleOrderNavigation(notification) {
      const orderId = notification?.data?.id
      if (!orderId) {
        return
      }
      this.handleNavigationToPath(notification, `/order/view/${orderId}`)
    },
    handleScheduleNotification(notification) {
      this.handleNavigationToPath(notification, '/dashboard')
    },
    handleAssignedNotification(notification) {
      this.handleOrderNavigation(notification)
    },
    handleOrderListNavigation(notification) {
      this.handleNavigationToPath(notification, '/order/list')
    },
    handleExternalOrderRequestNavigation(notification) {
      const requestId = notification?.data?.id
      if (!requestId) {
        return
      }

      if (notification.type === 'external_order_request_submitted') {
        this.handleNavigationToPath(notification, `/order-requests/review/${requestId}`)
        return
      }

      if (notification.type === 'external_order_request_changes_requested') {
        this.handleNavigationToPath(notification, `/partner/requests/${requestId}`)
      }
    },
    handleNavigationToPath(notification, path) {
      if (!path) {
        return
      }
      this.markAsRead(notification)
      if (path !== this.$router.currentRoute.path) {
        this.$router.push(path).catch(() => {})
      }
      this.$emit('action')
    },
    handleSystemVersionNotification(notification) {
      this.markAsRead(notification)
      this.$emit('action')
      this.reload()
    },
    formatScheduleChangeText(notification) {
      const shiftName = notification?.data?.shiftName || ''
      const timeFrom = this.formatScheduleDate(notification?.data?.timeFrom)
      const timeTo = this.formatScheduleDate(notification?.data?.timeTo)

      if (notification?.data?.changeType === 'added') {
        return this.sprintf(this.$t('notifications.schedule_changed.text-added'), shiftName, timeFrom, timeTo)
      }

      if (notification?.data?.changeType === 'removed') {
        return this.sprintf(this.$t('notifications.schedule_changed.text-removed'), shiftName, timeFrom, timeTo)
      }

      return this.$t('notifications.schedule_changed.text')
    },
    formatScheduleDate(value) {
      if (!value) {
        return ''
      }

      const date = dayjs(value)
      if (!date.isValid()) {
        return ''
      }

      return date.format('DD.MM.YYYY HH:mm')
    },
    reload() {
      // Plain reload to pick up the new app version. Do NOT clearAllState and do NOT
      // unregister service workers here: unregistering destroys the push subscription
      // and orphans the Beams device on the server (Pusher subscriber leak). The new SW
      // already activates via skipWaiting()/clients.claim().
      setTimeout(() => {
        window.location.reload(true)
      }, 250)
    },
  },
}
</script>
